<template>
  <AppDialog
    :show="show"
    :title="title"
    append-to-body
    class="paid-addon-cancelation cancellation-final-offer-dialog final-offer-banner"
    @close="toggleDialog"
  >
    <div
      v-if="loading"
      class="spinner d-flex align-items-center justify-content-center p-20"
    >
      <div class="spinner-wrapper absolute">
        <span class="hi hi-spinner hi-32 text-blue" />
      </div>
    </div>
    <div
      v-if="hasSubscriptionPeriod"
      :class="isTrial ? 'trial' : 'subscription'"
    >
      <h4 class="title">Are you sure?</h4>
      <span class="info">
        We're so confident you'll be more successful with the Hubstaff Insights add-on that we're providing you with a
        one-time offer:
      </span>
      <div class="banner banner-blue banner-lg d-flex align-items-end justify-content-between text-left">
        <div class="content d-flex flex-column mb-20 flex-grow gap-16">
          <div
            v-if="!isTrial"
            class="logo"
          >
            <img
              :src="insightsAssets.logo.src"
              class="addon-logo"
            />
          </div>
          <img
            v-if="!isTrial && isYearly"
            class="annual-illustration"
            :src="insightsAssets?.cancellation_flow?.illustration_10_percent.src"
          />
          <template v-else>
            <template v-if="!isTrial">
              <div
                class="old-price"
                :class="`old-price-${subscriptionPeriod}`"
                v-text="'$' + INSIGHTS_PRICING[subscriptionPeriod].old"
              />
              <div>
                <div class="price d-flex align-items-center">
                  ${{ INSIGHTS_PRICING[subscriptionPeriod].new }}
                  <div class="time-span">/ seat / month</div>
                </div>
                <div class="time-limit">for the next 3 months</div>
              </div>
            </template>
            <template v-else>
              <div class="trial-extension">
                <div class="d-flex align-items-center justify-content-center">
                  <span class="days highlight">30</span>
                  <div class="time-span">days</div>
                </div>
                <span class="free-extension">free trial extension</span>
              </div>
              <i class="hi hi-plus text-lighter" />
              <div v-if="isYearly">
                <div class="price d-flex align-items-center justify-content-center">
                  <div class="highlight">10%</div>
                  <div class="annual-discount">Annual discount across all seats</div>
                </div>
              </div>
              <div v-else>
                <div class="price d-flex align-items-center justify-content-center">
                  <div
                    class="old-price text-lighter"
                    :class="`old-price-${subscriptionPeriod}`"
                  >
                    ${{ INSIGHTS_PRICING[subscriptionPeriod].old }}
                  </div>
                  <div class="highlight">${{ INSIGHTS_PRICING[subscriptionPeriod].new }}</div>
                  <div class="time-span">/ seat / month</div>
                </div>
                <div class="time-limit">price reduction for 3 months</div>
              </div>
            </template>
          </template>
        </div>
        <img
          :src="insightsAssets?.cancellation_flow?.illustration_blue.src"
          class="illustration"
        />
      </div>
      <em class="note text-grey">* If you continue with cancellation, this offer will no longer be available.</em>
      <div class="modal-footer d-flex justify-content-end pt-30 flex-wrap p-0">
        <a
          class="btn btn-default"
          @click="next"
        >
          Continue cancellation
        </a>
        <a
          class="btn btn-primary"
          @click="applyOffers"
        >
          Claim offer
        </a>
      </div>
    </div>
  </AppDialog>
</template>

<script>
import { INSIGHTS_ASSETS as insightsAssets } from '~/app_constants';
import { PaidAddonsResource } from '~/resources';

const INSIGHTS_PRICING = {
  quarterly: {
    old: 2.75,
    new: 1.84,
  },
  monthly: {
    old: 3,
    new: 2,
  },
  yearly: {
    old: 3,
    new: 2,
  },
};

export default {
  name: 'CancellationFinalOfferDialog',
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    isTrial: {
      type: Boolean,
      required: true,
    },
    subscriptionPeriod: {
      type: String,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    INSIGHTS_PRICING,
    insightsAssets,
    loading: false,
  }),
  computed: {
    hasSubscriptionPeriod() {
      return this.subscriptionPeriod.length;
    },
    isYearly() {
      return this.subscriptionPeriod === 'yearly';
    },
  },
  methods: {
    next(event) {
      this.$emit('next', this.id, event);
    },
    toggleDialog(event) {
      this.$emit('toggleDialog', 'finalOffer', false, event);
    },
    async applyOffers() {
      const params = { organizationId: this.organizationId, addonSym: 'insights' };

      this.loading = true;
      if (this.isTrial) {
        await this.applyTrialExtension(params, false);
        await this.applyCoupon(params, 'combined_discount_and_trial_extension');
      } else {
        await this.applyCoupon(params, 'apply_discount');
      }
      this.loading = false;
    },
    async applyCoupon(params, offerType) {
      try {
        const response = await PaidAddonsResource.applyCoupon(params, { offer_type: offerType });

        if (response && response.reload) {
          window.location.reload();
        }
      } catch (error) {
        this.$appMessage(error || 'Unable to complete your request. Try again later.');
      }
    },
    async applyTrialExtension(params, reload = true) {
      try {
        const response = await PaidAddonsResource.extendTrial(params);

        if (response && response.success && reload) {
          window.location.reload();
        }

        if (response && response.errors) {
          if (reload) {
            this.$appMessage(response.errors[0] || 'Unable to complete your request.');
          } else {
            throw new Error(response.errors[0] || 'Unable to complete your request.');
          }
        }
      } catch (error) {
        this.$appMessage(error || 'Unable to complete your request. Try again later.');
      }
    },
  },
};
</script>
